import { useEffect, useRef, useState, ComponentType } from 'react';
import ReactDOM from 'react-dom';
import {
  SaveIcon,
  CloseIcon,
  EditIcon,
  ArchiveIcon,
  DuplicateIcon,
  ProgressIndicatorIcon,
  ProductGroupIcon,
  UnlinkIcon,
  DurationCounterIcon,
  DeleteIcon,
  MoreIcon,
  AddIcon,
} from '../../../../bombiix-icon';
import type { BombiixIconProps } from '../../../../bombiix-icon/types';
import NewCheckbox from '../../../../Components/PIM/common/NewCheckbox/NewCheckbox';
import { MoreAction, RowActionConfig } from './rowActions';
import { TableRow } from './types';
import './ProductTableView.css';

const ROW_ACTION_ICONS: Record<string, ComponentType<BombiixIconProps>> = {
  'bombiix-icon-save': SaveIcon,
  'bombiix-icon-close': CloseIcon,
  'bombiix-icon-edit': EditIcon,
  'bombiix-icon-archive': ArchiveIcon,
  'bombiix-icon-duplicate': DuplicateIcon,
  'bombiix-icon-progress-indicator': ProgressIndicatorIcon,
  'bombiix-icon-product-group': ProductGroupIcon,
  'bombiix-icon-link': UnlinkIcon,
  'bombiix-icon-duration-counter': DurationCounterIcon,
  'bombiix-icon-delete': DeleteIcon,
  'bombiix-icon-add': AddIcon,
};

/** Render a row-action icon as its master-design TSX component, falling back to the icon-font glyph. */
function RowActionIcon({ icon, size = 24 }: Readonly<{ icon: string; size?: number }>) {
  const IconComponent = ROW_ACTION_ICONS[icon];
  return IconComponent ? <IconComponent size={size} /> : <i className={icon} />;
}

interface RowMoreMenuProps {
  actions: MoreAction[];
  disabled?: boolean;
}

/**
 * 3-dot "more" menu rendered in the actions column. The menu is portalled to
 * <body> (AG Grid cells clip overflow) and positioned under the trigger.
 */
function RowMoreMenu({ actions, disabled = false }: Readonly<RowMoreMenuProps>) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 160 });
    }
    setOpen((value) => !value);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && !btnRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  if (!actions.length) return null;

  return (
    <>
      <button ref={btnRef} type="button" className="bombiix-icon-container" title="More actions" aria-label="More actions" disabled={disabled} onClick={toggle}>
        <MoreIcon size={24} />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div ref={menuRef} className="ptv-more-dropdown" style={{ position: 'fixed', top: pos.top, left: pos.left }}>
            {actions.map((action, index) =>
              action.divider ? (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`divider-${index}`} className="ptv-more-divider" />
              ) : (
                <button
                  key={action.label ?? `item-${index}`}
                  type="button"
                  className={`ptv-more-item${action.danger ? ' danger' : ''}`}
                  onClick={() => {
                    action.onClick?.();
                    setOpen(false);
                  }}
                >
                  {action.icon && <RowActionIcon icon={action.icon} />}
                  <span>{action.label}</span>
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

/** The Cluster toggle shown on a duplicate inline row, ahead of Save / Cancel. */
export interface ClusterControlConfig {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface RowActionsProps {
  row: TableRow;
  visibleActions: RowActionConfig[];
  moreActions: MoreAction[];
  /** Greys out every action on this row — used to freeze other rows while a bulk edit is open. */
  disabled?: boolean;
  clusterControl?: ClusterControlConfig | undefined;
}

/**
 * Actions column content for a single grid row: the visible icon actions (Edit,
 * Archive, Duplicate — or Save/Cancel on the inline row) followed by the 3-dot menu.
 * A duplicate inline row also carries the Cluster toggle, which the design places
 * before the icons. Passed to BombiixAGGrid via renderRowActionExtras.
 */
function RowActions({ row, visibleActions, moreActions, clusterControl, disabled = false }: Readonly<RowActionsProps>) {
  return (
    <div className="ptv-row-actions">
      {clusterControl && (
        <div className="ptv-cluster-toggle">
          <NewCheckbox
            id={`ptv-cluster-${String(row._id)}`}
            size="sm"
            label={clusterControl.label}
            checked={clusterControl.checked}
            onChange={(event) => clusterControl.onChange(event.target.checked)}
          />
        </div>
      )}
      {visibleActions.map((action) => (
        <button
          key={action.title ?? action.icon}
          type="button"
          className={`bombiix-icon-container${action.className ? ` ${action.className}` : ''}`}
          title={action.title}
          aria-label={action.title}
          disabled={disabled || action.disabled}
          onClick={() => action.onClick(row)}
        >
          <RowActionIcon icon={action.icon} size={24} />
        </button>
      ))}
      {moreActions.length ? <RowMoreMenu actions={moreActions} disabled={disabled} /> : null}
    </div>
  );
}

export default RowActions;
